import { FC } from 'react';
import { CheckIcon } from '@heroicons/react/solid';
import classNames from 'classnames';

interface AnimatedStatusIndicatorProps {
  isSigned: boolean;
  isCurrentSigner?: boolean;
  size?: 'sm' | 'md'; // small (h-3) or medium (h-4)
}

/**
 * Reusable animated status indicator for signatures
 * Shows green checkmark for signed, pulsing orange for current signer, or gray for pending
 */
const AnimatedStatusIndicator: FC<AnimatedStatusIndicatorProps> = ({
  isSigned,
  isCurrentSigner = false,
  size = 'md',
}) => {
  const circleClass = size === 'sm' ? 'h-3 w-3' : 'h-4 w-4';
  const iconClass = size === 'sm' ? 'h-1.5 w-1.5' : 'h-2.5 w-2.5';
  const borderWidth = size === 'sm' ? 'border' : 'border-2';

  const circleContent = isSigned ? (
    <div
      className={classNames(
        'flex shrink-0 items-center justify-center rounded-full border-green-100 bg-white',
        circleClass,
        borderWidth,
      )}
    >
      <CheckIcon className={classNames('text-green-100', iconClass)} />
    </div>
  ) : (
    <div
      className={classNames(
        'relative shrink-0 rounded-full bg-white',
        circleClass,
        borderWidth,
        isCurrentSigner ? 'border-orange-100' : 'border-neutral-105',
      )}
    >
      {isCurrentSigner && (
        <>
          <div
            className={classNames(
              'absolute inset-0 rounded-full border-orange-100 opacity-75',
              borderWidth,
            )}
            style={{
              animation: 'ping 2s cubic-bezier(0, 0, 0.2, 1) infinite',
            }}
          />
          <div
            className={classNames(
              'absolute inset-0 rounded-full border-orange-100 opacity-50',
              borderWidth,
            )}
            style={{
              animation: 'ping 2s cubic-bezier(0, 0, 0.2, 1) infinite',
              animationDelay: '1s',
            }}
          />
          <div
            className={classNames(
              'absolute inset-0 rounded-full border-orange-100 opacity-25',
              borderWidth,
            )}
            style={{
              animation: 'ping 2s cubic-bezier(0, 0, 0.2, 1) infinite',
              animationDelay: '2s',
            }}
          />
        </>
      )}
    </div>
  );

  return circleContent;
};

export default AnimatedStatusIndicator;
